'use client';
import { useId, useState, type FormEvent } from 'react';
import { Input } from 'components/UI/Input';
import Link from 'components/UI/Link/Link';
import { getZodiacSign, getZodiacSignUrl } from 'utils/zodiacSigns';
import styles from './date-of-birth.module.scss';

type DateResult = {
  readonly dueDate: Date;
  readonly zodiacSign: string;
  readonly formattedDate: string;
};

function calculateDueDate(lastPeriodDate: Date): Date {
  // Naegele-Regel: Erster Tag der letzten Periode - 3 Monate + 7 Tage + 1 Jahr
  const dueDate = new Date(lastPeriodDate);

  dueDate.setFullYear(dueDate.getFullYear() + 1);
  dueDate.setMonth(dueDate.getMonth() - 3);
  dueDate.setDate(dueDate.getDate() + 7);

  return dueDate;
}

export const WidgetToolsCalculatorDateOfBirth = () => {
  const dateId = useId();

  const [lastPeriodDate, setLastPeriodDate] = useState('');
  const [result, setResult] = useState<DateResult | null>(null);

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!lastPeriodDate) return;

    const inputDate = new Date(lastPeriodDate);
    const dueDate = calculateDueDate(inputDate);
    const zodiacSign = getZodiacSign(dueDate);
    const formattedDate = dueDate.toLocaleDateString('de-DE', {
      weekday: 'long',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });

    setResult({
      dueDate,
      zodiacSign,
      formattedDate,
    });
  }

  return (
    <div className={styles.root}>
      <form onSubmit={onSubmit}>
        <div className={styles.fieldWrapper}>
          <Input
            id={dateId}
            type="date"
            label="Datum der letzten Periode"
            value={lastPeriodDate}
            max={new Date().toISOString().split('T')[0]}
            onChange={(event) => {
              setLastPeriodDate(event.currentTarget.value);
            }}
            required
          />
        </div>

        <button className={styles.button} type="submit">
          Geburtstermin berechnen
        </button>
      </form>

      {result !== null && (
        <div className={styles.results} aria-live="polite">
          <div className={styles.headline}>Der errechnete Geburtstermin ist {result.formattedDate}</div>

          <div className={styles.headline}>
            Das Sternzeichen eures Kindes ist {result.zodiacSign} (
            <Link href={getZodiacSignUrl(result.zodiacSign)}>
              mehr {result.zodiacSign === 'Waage' || result.zodiacSign === 'Jungfrau' ? 'zur' : 'zum'}{' '}
              {result.zodiacSign}
            </Link>
            )
          </div>
        </div>
      )}
    </div>
  );
};
